import { useCallback, useRef, useState } from "react";
import {
  askAssistant,
  unavailableResult,
  type AssistantAnswer,
  type AssistantClientCtx,
} from "@/api/assistant";

export interface AssistantTurn {
  id: string;
  question: string;
  loading: boolean;
  result?: AssistantAnswer;
}

function newId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  return c?.randomUUID ? c.randomUUID() : `t-${Date.now()}-${Math.round(performance.now())}`;
}

/**
 * Сесійний тред помічника (без крос-сесійної пам'яті — TZ не-ціль). Кожен
 * `ask` додає хід, виставляє loading і резолвить станом проксі. Будь-який
 * throw (мережа/401) → state=unavailable (питання НЕ губиться — є «Повторити»).
 */
export function useAssistant(routeKey?: string) {
  const [thread, setThread] = useState<AssistantTurn[]>([]);
  const [asking, setAsking] = useState(false);
  const inFlight = useRef<AbortController | null>(null);

  const ask = useCallback(
    async (raw: string) => {
      const question = raw.trim();
      if (!question || asking) return;

      const id = newId();
      setThread((t) => [...t, { id, question, loading: true }]);
      setAsking(true);

      const ctx: AssistantClientCtx = routeKey ? { routeKey } : {};
      const ctrl = new AbortController();
      inFlight.current = ctrl;
      try {
        const result = await askAssistant(question, ctx, ctrl.signal);
        setThread((t) => t.map((x) => (x.id === id ? { ...x, loading: false, result } : x)));
      } catch {
        setThread((t) =>
          t.map((x) => (x.id === id ? { ...x, loading: false, result: unavailableResult() } : x)),
        );
      } finally {
        setAsking(false);
        inFlight.current = null;
      }
    },
    [asking, routeKey],
  );

  const reset = useCallback(() => {
    inFlight.current?.abort();
    setThread([]);
  }, []);

  return { thread, ask, asking, reset };
}
